import React from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import SEO from "./SEO";
import { FaUserTie, FaUsers, FaUserShield, FaClipboardList, FaSearch, FaHistory, FaAward } from "react-icons/fa";

const CorporateGovernance: React.FC = () => {
  const policySections = [
    {
      title: "Governance Framework",
      icon: <FaAward />,
      content: `The Corporate Governance Policy ('Policy') provides the framework under which the Board of Directors operates. It includes the Company's corporate structure, culture, policies and the manner in which it deals with various stakeholders. The Policy has been aligned with the Companies Act, 2013 ('Act'), SEBI (Listing Obligations and Disclosure Requirements) Regulations, 2015 ('SEBI Listing Regulations'), and RBI guidelines.`,
    },
    {
      title: "Board of Directors",
      icon: <FaUserTie />,
      list: [
        `Responsibility: The Board acts in the best interests of the Company and its stakeholders, adhering to a strict Code of Conduct.`,
        `Composition: An optimum combination of executive and non-executive directors, including at least one independent woman director.`,
        `Independence: At least 50% of the Board comprises non-executive directors to ensure unbiased oversight.`,
      ],
    },
    {
      title: "Board Meetings",
      icon: <FaUsers />,
      list: [
        "Frequency: Held at least four times a year, ensuring a maximum gap of 120 days for continuous oversight.",
        "Informed Oversight: Comprehensive statutory information is provided to all Directors for effective decision-making.",
      ],
    },
    {
      title: "Board Committees",
      icon: <FaClipboardList />,
      list: [
        "Audit Committee: Ensuring financial integrity with independent leadership.",
        "Nomination & Remuneration: Defining high-performance standards.",
        "Stakeholders Relationship: Protecting your interests and feedback.",
        "Risk Management: Maintaining institutional stability and foresight.",
        "CSR & IT Strategy: Driving social impact and technological innovation.",
        "ALCO: Expert management of liquidity and financial health.",
      ],
    },
    {
      title: "Transparency & Disclosures",
      icon: <FaSearch />,
      list: [
        "Regular reporting on risk management systems and policies.",
        "Full disclosure of Board composition and committee roles.",
        "Transparent Annual Financial Statements including credit ratings and ALM status.",
      ],
    },
    {
      title: "Vigil Mechanism",
      icon: <FaUserShield />,
      content: "We uphold a robust Whistleblower policy. Employees and stakeholders can report unethical behavior or fraud without fear of victimization, reinforcing our zero-tolerance policy towards misconduct.",
    },
    {
      title: "Statutory Standards",
      icon: <FaHistory />,
      content: "Our commitment to excellence includes statutory auditor rotation every three years and rigorous MD & CEO certification of financial controls, ensuring the highest level of institutional integrity.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO
        title="Corporate Governance"
        description={`The structural framework for stakeholder integrity at ${import.meta.env.VITE_COMPANY_NAME}. Compliance with RBI and SEBI governance guidelines.`}
        keywords={`corporate governance, board of directors, financial transparency, ${import.meta.env.VITE_COMPANY_NAME} compliance`}
      />

      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-gray-50/30 to-white" />

      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10 pt-44 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">

          <header className="mb-20">
            <div className="flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl w-fit rounded-full mb-10 transition-transform hover:scale-105">
              <div className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
              <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">Institutional Integrity</span>
            </div>
            <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-10 text-text-primary leading-[0.9]">
              Ethical <br />
              <span className="text-secondary">Governance.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-2xl font-medium leading-relaxed max-w-2xl">
              Our governance framework is designed to ensure absolute transparency, accountability, and the long-term protection of all stakeholder interests.
            </p>
          </header>

          <div className="space-y-12">
            {policySections.map((section, idx) => (
              <div key={idx} className="bg-white p-10 md:p-14 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl shrink-0 transition-colors group-hover:bg-primary group-hover:text-white">
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-4xl font-black mb-6 text-text-primary tracking-tight">
                      {section.title}
                    </h2>
                    {section.content && (
                      <p className="text-text-secondary text-lg md:text-xl leading-relaxed font-medium mb-6">
                        {section.content}
                      </p>
                    )}
                    {section.list && (
                      <ul className="grid grid-cols-1  gap-6 pt-4">
                        {section.list.map((li, i) => (
                          <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-base md:text-lg leading-relaxed bg-gray-50/50 p-6 rounded-2xl border border-gray-100 transition-all hover:bg-white hover:border-secondary/20 hover:shadow-md">
                            <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Regulatory Footer Card */}
            <div className="bg-primary text-white p-12 md:p-20 rounded-[3rem] shadow-2xl relative overflow-hidden mt-20">
              <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-[80px]" />
              <h3 className="text-2xl md:text-4xl font-black mb-10 tracking-tighter">Certified Compliance</h3>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed font-medium mb-12">
                Standardized by {import.meta.env.VITE_COMPANY_NBFC} in full compliance with the Master Direction - Non-Banking Financial Company - Systemically Important Non-Deposit taking Company and Deposit taking Company (Reserve Bank) Directions, 2016.
              </p>
              <div className="flex flex-wrap gap-8 pt-8 border-t border-white/20">
                <div className="text-[10px] font-black uppercase tracking-widest text-secondary">Board Approved April 2025</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-secondary">RBI Compliant Framework</div>
              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CorporateGovernance;
